"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.tabNavigationStaticClassName = exports.TabNavigation = void 0;
var _react = _interopRequireWildcard(require("react"));
require("./TabNavigation.css");
var _cdsCommon = require("@cbhq/cds-common");
var _useScaleDensity = require("@cbhq/cds-common/scale/useScaleDensity");
var _tabs = require("@cbhq/cds-common/tokens/tabs");
var _zIndex = require("@cbhq/cds-common/tokens/zIndex");
var _cdsUtils = require("@cbhq/cds-utils");
var _useDimensions2 = require("../hooks/useDimensions");
var _layout = require("../layout");
var _focus = require("../styles/focus");
var _PressableOpacity = require("../system/PressableOpacity");
var _ThemeProvider = require("../system/ThemeProvider");
var _linaria = require("../utils/linaria");
var _Paddle = require("./Paddle");
var _TabIndicator = require("./TabIndicator");
var _TabLabel = require("./TabLabel");
const _excluded = ["tabs", "value", "variant", "testID", "onChange", "background", "accessibilityLabelledBy", "accessibilityLabel", "Component", "gap", "role"],
  _excluded2 = ["label"];
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
const tabNavigationStaticClassName = 'cds-tab-navigation';
exports.tabNavigationStaticClassName = tabNavigationStaticClassName;
const SCROLL_PADDING = 5; // How much breathing room do we want before showing the paddles
const PRESS_TIMEOUT = 50;
let scrollTimeout;
const containerClassName = "cds-containerClassName-cffdzfs";
const scrollContainerClassName = "cds-scrollContainerClassName-s115yh2t";
const pressableClass = "cds-pressableClass-p19irxck";
const pressableClassWithCustomTab = "cds-pressableClassWithCustomTab-p863r8z";
const fallbackLayout = {
  width: 0,
  x: 0
};
const TabNavigation = /*#__PURE__*/(0, _react.memo)( /*#__PURE__*/(0, _react.forwardRef)((_ref, forwardedRef) => {
  var _scrollRef$current, _scrollRef$current2, _scrollRef$current3;
  let tabs = _ref.tabs,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? tabs[0].id : _ref$value,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'primary' : _ref$variant,
    _ref$testID = _ref.testID,
    testID = _ref$testID === void 0 ? 'tabNavigation' : _ref$testID,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? _cdsUtils.noop : _ref$onChange,
    background = _ref.background,
    accessibilityLabelledBy = _ref.accessibilityLabelledBy,
    accessibilityLabel = _ref.accessibilityLabel,
    Component = _ref.Component,
    _ref$gap = _ref.gap,
    gap = _ref$gap === void 0 ? 4 : _ref$gap,
    _ref$role = _ref.role,
    role = _ref$role === void 0 ? 'tablist' : _ref$role,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const _useState = (0, _react.useState)(fallbackLayout),
    activeTabLayout = _useState[0],
    setActiveTabLayout = _useState[1];
  const _useDimensions = (0, _useDimensions2.useDimensions)(),
    observe = _useDimensions.observe,
    width = _useDimensions.width;
  const isDense = (0, _useScaleDensity.useScaleDensity)() === 'dense';
  const isPrimary = (0, _react.useMemo)(() => variant === 'primary', [variant]);
  const shouldOverrideScale = (0, _react.useMemo)(() => isDense && isPrimary, [isDense, isPrimary]);
  const offsetRef = (0, _react.useRef)(0);
  const tabsRefs = (0, _react.useRef)(tabs.map(({
    id
  }) => ({
    id,
    ref: /*#__PURE__*/(0, _react.createRef)()
  })));
  const scrollRef = (0, _react.useRef)(null);
  (0, _react.useImperativeHandle)(forwardedRef, () => scrollRef.current);
  const end = Number((_scrollRef$current = scrollRef.current) === null || _scrollRef$current === void 0 ? void 0 : _scrollRef$current.scrollWidth) - Number((_scrollRef$current2 = scrollRef.current) === null || _scrollRef$current2 === void 0 ? void 0 : _scrollRef$current2.offsetWidth);
  const canScrollRight = Number((_scrollRef$current3 = scrollRef.current) === null || _scrollRef$current3 === void 0 ? void 0 : _scrollRef$current3.scrollLeft) < end;
  const _useToggler = (0, _cdsCommon.useToggler)(false),
    leftPaddle = _useToggler[0],
    toggleLeftPaddle = _useToggler[1];
  const _useToggler2 = (0, _cdsCommon.useToggler)(canScrollRight),
    rightPaddle = _useToggler2[0],
    toggleRightPaddle = _useToggler2[1];
  const descendantAriaRole = role === 'tablist' ? 'tab' : 'radio';
  const roleBasedA11yProps = role === 'radiogroup' ? {
    'aria-activedescendant': `tab--${value}`
  } : undefined;
  const handleOnScroll = (0, _react.useCallback)(() => {
    var _scrollRef$current4;
    const scrollDistance = Number((_scrollRef$current4 = scrollRef.current) === null || _scrollRef$current4 === void 0 ? void 0 : _scrollRef$current4.scrollLeft);
    const endTrigger = end - SCROLL_PADDING;
    const startTrigger = SCROLL_PADDING;

    // Hide/show the left paddle
    if (scrollDistance > startTrigger && !leftPaddle) {
      toggleLeftPaddle.toggleOn();
    }
    if (scrollDistance <= startTrigger && leftPaddle) {
      toggleLeftPaddle.toggleOff();
    }

    // Hide/show the right paddle
    if (scrollDistance < endTrigger && !rightPaddle) {
      toggleRightPaddle.toggleOn();
    }
    if (scrollDistance >= endTrigger && rightPaddle) {
      toggleRightPaddle.toggleOff();
    }
  }, [end, leftPaddle, rightPaddle, toggleLeftPaddle, toggleRightPaddle]);

  /** ⚡️ Side effects */
  (0, _react.useEffect)(() => {
    handleOnScroll();
  }, [handleOnScroll, width]);
  const handleScrollLeft = (0, _react.useCallback)(() => {
    var _scrollRef$current5;
    scrollRef === null || scrollRef === void 0 ? void 0 : (_scrollRef$current5 = scrollRef.current) === null || _scrollRef$current5 === void 0 ? void 0 : _scrollRef$current5.scrollTo({
      left: 0,
      behavior: 'smooth'
    });
  }, [scrollRef]);
  const handleScrollRight = (0, _react.useCallback)(() => {
    var _scrollRef$current6;
    scrollRef === null || scrollRef === void 0 ? void 0 : (_scrollRef$current6 = scrollRef.current) === null || _scrollRef$current6 === void 0 ? void 0 : _scrollRef$current6.scrollTo({
      left: end,
      behavior: 'smooth'
    });
  }, [end]);

  /** 🛼
   *  Recreating handleLayout every time value changes will
   *  cause the active TabLabel to call setActiveTabLayout
   *  with its layout values, updating the TabIndicator.
   */
  const handleLayout = (0, _react.useCallback)((id, layout) => {
    // Track offset
    if (id === tabs[0].id) offsetRef.current = layout.x;
    if (id === value) {
      setActiveTabLayout({
        width: layout.width,
        x: layout.x - offsetRef.current
      });
    }
  }, [tabs, value]);
  const getTabKeydownHandler = (0, _react.useCallback)(currentId => {
    return function handleKeyDown(e) {
      var _refs$findIndex, _refs$nextIndex$ref, _refs$nextIndex$ref$c, _refs$prevIndex$ref, _refs$prevIndex$ref$c;
      const refs = tabsRefs === null || tabsRefs === void 0 ? void 0 : tabsRefs.current;
      const currentActiveIndex = (_refs$findIndex = refs === null || refs === void 0 ? void 0 : refs.findIndex(({
        id
      }) => id === currentId)) !== null && _refs$findIndex !== void 0 ? _refs$findIndex : 0;
      const lastIndex = Number(refs === null || refs === void 0 ? void 0 : refs.length) - 1;
      const nextIndex = currentActiveIndex < lastIndex ? currentActiveIndex + 1 : 0;
      const prevIndex = currentActiveIndex !== 0 ? currentActiveIndex - 1 : lastIndex;
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          refs === null || refs === void 0 ? void 0 : (_refs$nextIndex$ref = refs[nextIndex].ref) === null || _refs$nextIndex$ref === void 0 ? void 0 : (_refs$nextIndex$ref$c = _refs$nextIndex$ref.current) === null || _refs$nextIndex$ref$c === void 0 ? void 0 : _refs$nextIndex$ref$c.focus();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          refs === null || refs === void 0 ? void 0 : (_refs$prevIndex$ref = refs[prevIndex].ref) === null || _refs$prevIndex$ref === void 0 ? void 0 : (_refs$prevIndex$ref$c = _refs$prevIndex$ref.current) === null || _refs$prevIndex$ref$c === void 0 ? void 0 : _refs$prevIndex$ref$c.focus();
          break;
        default:
          break;
      }
    };
  }, []);
  const getScrollIntoViewHandler = (0, _react.useCallback)(ref => {
    return function handleFocus() {
      var _ref$current, _ref$current2;
      // Container
      const container = scrollRef.current;
      const containerScrollLeft = Number(container === null || container === void 0 ? void 0 : container.scrollLeft);
      const containerWidth = Number(container === null || container === void 0 ? void 0 : container.getBoundingClientRect().width);
      const containerRightEdge = containerWidth + Number(container === null || container === void 0 ? void 0 : container.scrollLeft);
      // Element
      const elementWidth = Number(ref === null || ref === void 0 ? void 0 : (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.clientWidth);
      const elementOffsetLeft = Number(ref === null || ref === void 0 ? void 0 : (_ref$current2 = ref.current) === null || _ref$current2 === void 0 ? void 0 : _ref$current2.offsetLeft);
      // State
      const isOffscreenLeft = elementOffsetLeft < containerScrollLeft + _tabs.tabsPaddleWidth;
      const isOffscreenRight = elementOffsetLeft + elementWidth > containerRightEdge - _tabs.tabsPaddleWidth;
      // Set the new scroll location
      let newPosition;
      if (isOffscreenLeft) newPosition = elementOffsetLeft - _tabs.tabsPaddleWidth;
      if (isOffscreenRight) newPosition = elementWidth + elementOffsetLeft + _tabs.tabsPaddleWidth - containerWidth;

      // SCROLL TO THE NEW POSITION
      // Wrapped in a timeout so the scroll event doesn't fire before the press handler
      scrollTimeout = setTimeout(() => {
        if (newPosition && (isOffscreenLeft || isOffscreenRight)) {
          container === null || container === void 0 ? void 0 : container.scrollTo({
            left: newPosition,
            behavior: 'smooth'
          });
        }
      }, PRESS_TIMEOUT);
    };
  }, []);
  const getTabPressHandler = (0, _react.useCallback)((id, onPress) => {
    return function handleTabPress() {
      clearTimeout(scrollTimeout);
      onChange(id);
      onPress === null || onPress === void 0 ? void 0 : onPress(id); // handle callback
    };
  }, [onChange]);
  const getChildren = (0, _react.useCallback)(_ref2 => {
    let label = _ref2.label,
      props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
    return /*#__PURE__*/_react.default.createElement(_TabLabel.TabLabel, _extends({
      active: props.id === value
    }, props, {
      onLayout: handleLayout,
      variant: variant
    }), label);
  }, [handleLayout, value, variant]);

  // Iterate over the tabs and create Pressable TabLabels
  const tabLabels = (0, _react.useMemo)(() => {
    var _tabs$filter;
    return tabs === null || tabs === void 0 ? void 0 : (_tabs$filter = tabs.filter(Boolean)) === null || _tabs$filter === void 0 ? void 0 : _tabs$filter.map(({
      id,
      onPress,
      label,
      accessibilityLabel = label,
      count,
      testID: tabLabelTestID = `${testID}-tabLabel--${id}`,
      Component: TabComponent
    }) => {
      var _tabsRefs$current$fin, _tabsRefs$current, _tabsRefs$current$fin2;
      const isActiveTab = id === value;
      const roleBasedTabA11yProps = role === 'radiogroup' ? {
        'aria-checked': isActiveTab
      } : {
        'aria-selected': isActiveTab
      };
      const currentRef = (_tabsRefs$current$fin = (_tabsRefs$current = tabsRefs.current) === null || _tabsRefs$current === void 0 ? void 0 : (_tabsRefs$current$fin2 = _tabsRefs$current.find(tab => tab.id === id)) === null || _tabsRefs$current$fin2 === void 0 ? void 0 : _tabsRefs$current$fin2.ref) !== null && _tabsRefs$current$fin !== void 0 ? _tabsRefs$current$fin : /*#__PURE__*/(0, _react.createRef)();
      const CustomTabComponent = TabComponent !== null && TabComponent !== void 0 ? TabComponent : Component;
      return /*#__PURE__*/_react.default.createElement(_PressableOpacity.PressableOpacity, _extends({
        key: `${id}--button`,
        ref: currentRef,
        accessibilityLabel: typeof accessibilityLabel === 'string' ? accessibilityLabel : undefined,
        "aria-controls": `tabpanel--${id}`
      }, roleBasedTabA11yProps, {
        className: (0, _linaria.cx)(tabNavigationStaticClassName, CustomTabComponent ? pressableClassWithCustomTab : pressableClass, _focus.insetFocusRing),
        id: `tab--${id}`,
        onFocus: getScrollIntoViewHandler(currentRef),
        onKeyDown: getTabKeydownHandler(id),
        onPress: getTabPressHandler(id, onPress),
        role: descendantAriaRole,
        tabIndex: isActiveTab ? undefined : -1,
        testID: tabLabelTestID
      }), CustomTabComponent && variant !== 'primary' ? /*#__PURE__*/_react.default.createElement(CustomTabComponent, {
        active: isActiveTab,
        id: id,
        label: label
      }) : getChildren({
        id,
        count,
        label,
        Component: CustomTabComponent
      }));
    });
  }, [tabs, testID, value, role, Component, getScrollIntoViewHandler, getTabKeydownHandler, getTabPressHandler, descendantAriaRole, variant, getChildren]);
  return /*#__PURE__*/_react.default.createElement("div", {
    ref: observe,
    className: (0, _linaria.cx)(tabNavigationStaticClassName, containerClassName)
  }, /*#__PURE__*/_react.default.createElement(_Paddle.Paddle, {
    accessibilityLabel: "Skip to beginning",
    background: background,
    onPress: handleScrollLeft,
    show: leftPaddle,
    testID: `${tabNavigationStaticClassName}--paddle-left`,
    variant: variant
  }), /*#__PURE__*/_react.default.createElement(_layout.HStack, {
    ref: scrollRef,
    alignItems: "center",
    background: background,
    className: (0, _linaria.cx)(tabNavigationStaticClassName, scrollContainerClassName),
    onScroll: handleOnScroll,
    position: "relative"
  }, /*#__PURE__*/_react.default.createElement(_layout.VStack, _extends({
    testID: testID
  }, rest, {
    spacing: 0
  }), shouldOverrideScale ? /*#__PURE__*/_react.default.createElement("span", {
    style: {
      zIndex: _zIndex.zIndex.navigation
    }
  }, /*#__PURE__*/_react.default.createElement(_ThemeProvider.ThemeProvider, {
    scale: "large"
  }, /*#__PURE__*/_react.default.createElement(_layout.HStack, _extends({
    accessibilityLabel: accessibilityLabel,
    accessibilityLabelledBy: accessibilityLabelledBy,
    flexShrink: 0,
    gap: gap,
    role: role
  }, roleBasedA11yProps), tabLabels))) : /*#__PURE__*/_react.default.createElement(_layout.HStack, _extends({
    accessibilityLabel: accessibilityLabel,
    accessibilityLabelledBy: accessibilityLabelledBy,
    flexShrink: 0,
    gap: gap,
    role: role,
    zIndex: _zIndex.zIndex.navigation
  }, roleBasedA11yProps), tabLabels), isPrimary && (activeTabLayout === null || activeTabLayout === void 0 ? void 0 : activeTabLayout.width) > 0 && /*#__PURE__*/_react.default.createElement(_TabIndicator.TabIndicator, _extends({
    background: background
  }, activeTabLayout)))), /*#__PURE__*/_react.default.createElement(_Paddle.Paddle, {
    accessibilityLabel: "skip to end",
    background: background,
    direction: "right",
    onPress: handleScrollRight,
    show: rightPaddle,
    testID: `${tabNavigationStaticClassName}--paddle-right`,
    variant: variant
  }));
}));
exports.TabNavigation = TabNavigation;
TabNavigation.displayName = 'TabNavigation';